// eslint-disable-next-line @typescript-eslint/no-explicit-any
function clerkGlobal(): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).Clerk
}

/** First org membership that isn't `excludeOrgId`, or null if none is left. */
export async function firstOtherOrg(excludeOrgId?: string): Promise<string | null> {
  try {
    const memberships = await clerkGlobal()?.user?.getOrganizationMemberships()
    const match = memberships?.data?.find(
      (m: { organization: { id: string } }) => m.organization.id !== excludeOrgId,
    )
    return match?.organization?.id ?? null
  } catch {
    return null
  }
}

/** Set (or clear) the active org. Await before navigating — the next request reads it. */
export async function setActiveOrg(orgId: string | null): Promise<void> {
  try {
    await clerkGlobal()?.setActive?.({ organization: orgId })
  } catch {
    // Best-effort: the backend ignores an X-Org-Id you aren't a member of.
  }
}

// Every API request awaits this, and on a cold load they all arrive before
// Clerk has picked an org: dedupe to one activation, not one per request.
let activating: Promise<void> | null = null

// If no org is active but the user has one, activate it so requests carry an
// X-Org-Id. Without it the backend guesses (oldest org) — the switch-does-nothing
// bug. An already-active org (invite flow / last selection) is left as-is.
export function ensureActiveOrg(): Promise<void> {
  if (!activating) {
    // Best-effort, like the helpers above: every API request awaits this, so a
    // failure here must never be the reason a request doesn't go out.
    activating = activateOrg()
      .catch(() => {})
      .finally(() => { activating = null })
  }
  return activating
}

async function activateOrg(): Promise<void> {
  const clerk = clerkGlobal()
  if (!clerk) return

  if (!clerk.loaded) {
    await new Promise<void>((resolve) => {
      const unsub = clerk.addListener(() => {
        if (clerk.loaded) {
          unsub?.()
          resolve()
        }
      })
    })
  }

  if (!clerk.user || clerk.organization) return // no user, or an org is already active

  const first = await firstOtherOrg()
  if (first) await setActiveOrg(first)
}
